import React, { useState, useRef, useEffect } from 'react';
import { 
  FaLeaf, 
  FaWind, 
  FaCloudRain, 
  FaThermometerHalf,
  FaExclamationTriangle,
  FaSync,
  FaCheckCircle,
  FaGlobe,
  FaEnvelope,
  FaChartBar,
  FaTachometerAlt,
  FaChartLine,
  FaDownload,
  FaCalendarAlt,
  FaFileExport
} from 'react-icons/fa';
import useSensorData from '../hooks/useSensorData';
import sensorService from '../services/sensorService';
import NPKChart from './charts/NPKChart';
import WeatherChart from './charts/WeatherChart';
import SpeedometerChart from './charts/SpeedometerChart';
import TrendChart from './charts/TrendChart';
import '../styles/SensorDashboard.css';

const SensorDashboard = () => {
  const { dashboardData, historicalData, loading, error, lastUpdated, refreshData } = useSensorData(true, 30000);
  const [viewMode, setViewMode] = useState('current'); // 'current', 'speedometer', 'trends'
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [fromDate, setFromDate] = useState(new Date(new Date().setDate(new Date().getDate() - 7)));
  const [toDate, setToDate] = useState(new Date());
  const datePickerRef = useRef(null);
  
  // Helper function to convert UTC to IST and format timestamp
  const formatTimestampToIST = (timestamp) => {
    const date = new Date(timestamp);
    
    // Convert to IST (UTC+5:30)
    const options = {
      timeZone: 'Asia/Kolkata',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    };
    
    return date.toLocaleString('en-IN', options).replace(',', '');
  };
  
  // Function to download current data as CSV in transposed format
  const downloadCurrentData = () => {
    try {
      setIsDownloading(true);
      
      if (!dashboardData || !dashboardData.sensorData) {
        alert('No current data available');
        setIsDownloading(false);
        return;
      }
    
      const { soilNutrients, weather, soilMetrics, airMetrics } = dashboardData.sensorData;
      const timestamp = dashboardData.sensorData.timestamp;
    
      // Helper function to escape CSV values
      const escapeCSV = (value) => {
        if (value == null) return '';
        value = String(value);
        // If the value contains a comma, newline, or double-quote, enclose it in double-quotes
        if (value.includes(',') || value.includes('\n') || value.includes('"')) {
          // Replace any double-quotes with two double-quotes
          value = value.replace(/"/g, '""');
          return `"${value}"`;
        }
        return value;
      };
    
      // Define all parameters and their units for the header
      const parameters = [
        { name: 'Timestamp', unit: '' },
        { name: 'Nitrogen', unit: 'mg/kg' },
        { name: 'Phosphorous', unit: 'mg/kg' },
        { name: 'Potassium', unit: 'mg/kg' },
        { name: 'Soil EC', unit: 'μS/cm' },
        { name: 'Soil Temperature', unit: '°C' },
        { name: 'Soil Moisture', unit: '%' },
        { name: 'Soil pH', unit: 'pH' },
        { name: 'Wind Speed', unit: 'km/h' },
        { name: 'Wind Direction', unit: '' },
        { name: 'Total Rainfall', unit: 'mm' },
        { name: 'Air Temperature', unit: '°C' },
        { name: 'Air Humidity', unit: '%' },
        { name: 'VPD', unit: 'kPa' },
        { name: 'Solar Radiation', unit: 'W/m²' }
      ];
    
      // Create CSV content with headers
      let csvContent = '';
      
      // Header row: Parameter names with units
      csvContent += parameters.map(param => {
        if (param.unit && param.unit !== '') {
          return escapeCSV(`${param.name} (${param.unit})`);
        }
        return escapeCSV(param.name);
      }).join(',') + '\n';
    
      // Create a single row with all current data
      const formattedTimestamp = formatTimestampToIST(timestamp);
      const row = [
        escapeCSV(formattedTimestamp),
        escapeCSV(soilNutrients ? soilNutrients.nitrogen : ''),
        escapeCSV(soilNutrients ? soilNutrients.phosphorous : ''),
        escapeCSV(soilNutrients ? soilNutrients.potassium : ''),
        escapeCSV(soilMetrics ? soilMetrics.soilEC : ''),
        escapeCSV(soilMetrics ? soilMetrics.soiltemp : ''),
        escapeCSV(soilMetrics ? soilMetrics.soilmoisture : ''),
        escapeCSV(soilMetrics ? soilMetrics.soilPH : ''),
        escapeCSV(weather ? weather.windSpeed : ''),
        escapeCSV(weather ? weather.windDirection : ''),
        escapeCSV(weather ? weather.totalRainfall : ''),
        escapeCSV(airMetrics ? airMetrics.airTemp : ''),
        escapeCSV(airMetrics ? airMetrics.airHumidity : ''),
        escapeCSV(airMetrics ? airMetrics.vpd : ''),
        escapeCSV(airMetrics ? airMetrics.solarRad : '')
      ];
      
      csvContent += row.join(',') + '\n';
    
      // Create proper CSV Blob with UTF-8 BOM for Excel compatibility
      const BOM = '\uFEFF'; // UTF-8 BOM
      const csvContentWithBOM = BOM + csvContent; // Include the full content with header
      const blob = new Blob([csvContentWithBOM], { type: 'text/csv;charset=utf-8' });
      
      // Create download link using Blob URL
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      
      // Format current date in IST for filename
      const currentDateIST = new Date().toLocaleString('en-IN', {
        timeZone: 'Asia/Kolkata',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      }).split(',')[0].replace(/\//g, '-');
      
      link.setAttribute('download', `sensor_data_${currentDateIST}.csv`);
      document.body.appendChild(link);
      link.click();
      
      // Clean up
      setTimeout(() => {
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }, 100);
    } catch (error) {
      console.error('Error downloading current data:', error);
      alert(`Error: ${error.message}`);
    } finally {
      setIsDownloading(false);
    }
  };
  
  // Add a loading state for the download button
  const [isDownloading, setIsDownloading] = useState(false);
  
  // Function to download historical data as CSV in transposed format
  const downloadHistoricalData = async () => {
    try {
      setIsDownloading(true);
      
      // Prepare date range
      const fromDateObj = new Date(fromDate);
      fromDateObj.setHours(0, 0, 0, 0);
      
      const toDateObj = new Date(toDate);
      toDateObj.setHours(23, 59, 59, 999);
      
      console.log(`Fetching data from ${fromDateObj.toISOString()} to ${toDateObj.toISOString()}`);
      
      // Fetch data for the specific date range
      const response = await sensorService.getDateRangeSensorData(fromDateObj, toDateObj);
      
      console.log('API Response:', response);
      
      // Check if we have data
      if (!response.data || response.data.length === 0) {
        console.warn('No data available for the selected date range');
        alert('No data available for the selected date range');
        setIsDownloading(false);
        return;
      }
      
      console.log(`Found ${response.data.length} records for the selected date range`);
      
      // Use the data from the API response
      let filteredData = response.data;
      
      // Remove duplicate data entries by comparing actual sensor values
      const uniqueDataEntries = [];
      const seenDataSignatures = new Set();
      
      // Function to create a signature for each data entry based on its values
      const createDataSignature = (data) => {
        return [
          data.nitrogen,
          data.phosphorous,
          data.potassium,
          data.soilEC,
          data.soiltemp,
          data.soilmoisture,
          data.soilPH,
          data.windSpeed,
          data.windDirection,
          data.totalRainfall,
          data.airTemp,
          data.airHumidity,
          data.vpd,
          data.solarRad
        ].join('|');
      };
      
      // Filter out duplicate data entries
      filteredData.forEach(data => {
        const signature = createDataSignature(data);
        if (!seenDataSignatures.has(signature)) {
          seenDataSignatures.add(signature);
          uniqueDataEntries.push(data);
        }
      });
      
      // Replace filtered data with unique entries
      filteredData = uniqueDataEntries;
      
      // Sort by timestamp (oldest first)
      filteredData.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
      
      // Helper function to escape CSV values
      const escapeCSV = (value) => {
        if (value == null) return '';
        value = String(value);
        // If the value contains a comma, newline, or double-quote, enclose it in double-quotes
        if (value.includes(',') || value.includes('\n') || value.includes('"')) {
          // Replace any double-quotes with two double-quotes
          value = value.replace(/"/g, '""');
          return `"${value}"`;
        }
        return value;
      };
      
      // Define all parameters and their units for the header
      const parameters = [
        { name: 'Timestamp', unit: '' },
        { name: 'Nitrogen', unit: 'mg/kg' },
        { name: 'Phosphorous', unit: 'mg/kg' },
        { name: 'Potassium', unit: 'mg/kg' },
        { name: 'Soil EC', unit: 'μS/cm' },
        { name: 'Soil Temperature', unit: '°C' },
        { name: 'Soil Moisture', unit: '%' },
        { name: 'Soil pH', unit: 'pH' },
        { name: 'Wind Speed', unit: 'km/h' },
        { name: 'Wind Direction', unit: '' },
        { name: 'Total Rainfall', unit: 'mm' },
        { name: 'Air Temperature', unit: '°C' },
        { name: 'Air Humidity', unit: '%' },
        { name: 'VPD', unit: 'kPa' },
        { name: 'Solar Radiation', unit: 'W/m²' }
      ];
      
      // Create CSV content with headers
      let csvContent = '';
      
      // Header row: Parameter names with units
      csvContent += parameters.map(param => {
        if (param.unit && param.unit !== '') {
          return escapeCSV(`${param.name} (${param.unit})`);
        }
        return escapeCSV(param.name);
      }).join(',') + '\n';
      
      // Data rows - one row per timestamp
      filteredData.forEach(data => {
        const formattedTimestamp = formatTimestampToIST(data.timestamp);
        
        // Create a row with all parameters for this timestamp
        const row = [
          escapeCSV(formattedTimestamp),
          escapeCSV(data.nitrogen),
          escapeCSV(data.phosphorous),
          escapeCSV(data.potassium),
          escapeCSV(data.soilEC),
          escapeCSV(data.soiltemp),
          escapeCSV(data.soilmoisture),
          escapeCSV(data.soilPH),
          escapeCSV(data.windSpeed),
          escapeCSV(data.windDirection),
          escapeCSV(data.totalRainfall),
          escapeCSV(data.airTemp),
          escapeCSV(data.airHumidity),
          escapeCSV(data.vpd),
          escapeCSV(data.solarRad)
        ];
        
        csvContent += row.join(',') + '\n';
      });
      
      // Create proper CSV Blob with UTF-8 BOM for Excel compatibility
      const BOM = '\uFEFF'; // UTF-8 BOM
      const csvContentWithBOM = BOM + csvContent; // Include the full content with header
      const blob = new Blob([csvContentWithBOM], { type: 'text/csv;charset=utf-8' });
      
      // Create download link using Blob URL
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      
      // Format date range in IST for filename
      const fromDateIST = fromDate.toLocaleString('en-IN', {
        timeZone: 'Asia/Kolkata',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      }).split(',')[0].replace(/\//g, '-');
      
      const toDateIST = toDate.toLocaleString('en-IN', {
        timeZone: 'Asia/Kolkata',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      }).split(',')[0].replace(/\//g, '-');
      
      link.setAttribute('download', `historical_sensor_data_${fromDateIST}_to_${toDateIST}.csv`);
      
      document.body.appendChild(link);
      link.click();
      
      // Clean up
      setTimeout(() => {
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }, 100);
      
    } catch (error) {
      console.error('Error downloading historical data:', error);
      
      // Provide more specific error messages based on the error type
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        console.error('Server error details:', error.response.data);
        alert(`Server error: ${error.response.status} - ${error.response.data.message || 'Unknown error'}`);
      } else if (error.request) {
        // The request was made but no response was received
        console.error('Network error - no response received');
        alert('Network error: Unable to connect to the server. Please check your internet connection.');
      } else {
        // Something happened in setting up the request that triggered an Error
        alert(`Error: ${error.message}`);
      }
    } finally {
      setIsDownloading(false);
    }
  };
  
  // Handle from date change
  const handleFromDateChange = (e) => {
    const newFromDate = new Date(e.target.value);
    setFromDate(newFromDate);
    
    // Ensure toDate is not before fromDate
    if (newFromDate > toDate) {
      setToDate(newFromDate);
    }
  };
  
  // Handle to date change
  const handleToDateChange = (e) => {
    const newToDate = new Date(e.target.value);
    setToDate(newToDate);
    
    // Ensure fromDate is not after toDate
    if (newToDate < fromDate) {
      setFromDate(newToDate);
    }
  };
  
  // Toggle date picker
  const toggleDatePicker = () => {
    setShowDatePicker(!showDatePicker);
  };
  
  // Handle click outside date picker to close it
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (datePickerRef.current && !datePickerRef.current.contains(event.target) && 
          !event.target.closest('.download-button')) {
        setShowDatePicker(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [datePickerRef]);

  if (loading && !dashboardData) {
    return (
      <div className="sensor-dashboard">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <h2>Loading Sensor Data...</h2>
          <p>Connecting to IoT device</p>
        </div>
      </div>
    );
  }

  if (error && !dashboardData) {
    return (
      <div className="sensor-dashboard">
        <div className="error-container">
          <FaExclamationTriangle className="error-icon" />
          <h2>Connection Error</h2>
          <p className="error-message">{error}</p>
          <button className="retry-button" onClick={refreshData}>
            <FaSync /> Retry Connection
          </button>
        </div>
      </div>
    );
  }

  const { sensorData, healthStatus, analytics } = dashboardData || {};
  const { soilNutrients, weather, soilMetrics, airMetrics } = sensorData || {};
  const { npkBalance, weatherAnalysis } = analytics || {};

  const getStatusColor = (status) => {
    switch (status) {
      case 'healthy':
      case 'good':
        return 'status-healthy';
      case 'warning':
      case 'questionable':
        return 'status-warning';
      case 'error':
      case 'poor':
        return 'status-error';
      default:
        return 'status-warning';
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'Unknown';
    return new Date(timestamp).toLocaleString();
  };

  return (
    <div className="sensor-dashboard">
      <div className="dashboard-container">
        {/* Header */}
        <div className="dashboard-header">
          <p className="dashboard-subtitle">
            IoT Sensor Dashboard for Real-time Soil & Weather Monitoring
          </p>
          
          {/* View Toggle */}
          <div className="view-toggle">
            <button 
              className={`view-toggle-button ${viewMode === 'current' ? 'active' : ''}`}
              onClick={() => setViewMode('current')}
            >
              <FaChartBar /> Current View
            </button>
            <button 
              className={`view-toggle-button ${viewMode === 'speedometer' ? 'active' : ''}`}
              onClick={() => setViewMode('speedometer')}
            >
              <FaTachometerAlt /> Speedometer
            </button>
            <button 
              className={`view-toggle-button ${viewMode === 'trends' ? 'active' : ''}`}
              onClick={() => setViewMode('trends')}
            >
              <FaChartLine /> Trends
            </button>
          </div>
        </div>

        {/* Render different views based on viewMode */}
        {viewMode === 'current' && (
          <div className="dashboard-grid dashboard-grid-current">
            {/* NPK Soil Nutrients Card */}
            <div className="dashboard-card dashboard-card-soil">
              <div className="card-header">
                <div className="card-icon" style={{ background: 'linear-gradient(135deg, #4CAF50, #45a049)' }}>
                  <FaLeaf />
                </div>
                <h2 className="card-title">Soil Nutrients (NPK)</h2>
                <span className={`status-indicator ${getStatusColor(healthStatus?.dataQuality)}`}>
                  <FaCheckCircle style={{ marginRight: '0.5rem' }} />
                  {healthStatus?.dataQuality || 'Unknown'}
                </span>
              </div>
              
              {soilNutrients && (
                <div className="npk-grid">
                  <div className="npk-item">
                    <div className="npk-label">Nitrogen (N)</div>
                    <div className="npk-value npk-nitrogen">{soilNutrients.nitrogen} <span className="unit">mg/kg</span></div>
                    <div className="npk-percentage">{npkBalance?.nitrogen?.percentage || 0}%</div>
                  </div>
                  <div className="npk-item">
                    <div className="npk-label">Phosphorous (P)</div>
                    <div className="npk-value npk-phosphorous">{soilNutrients.phosphorous} <span className="unit">mg/kg</span></div>
                    <div className="npk-percentage">{npkBalance?.phosphorous?.percentage || 0}%</div>
                  </div>
                  <div className="npk-item">
                    <div className="npk-label">Potassium (K)</div>
                    <div className="npk-value npk-potassium">{soilNutrients.potassium} <span className="unit">mg/kg</span></div>
                    <div className="npk-percentage">{npkBalance?.potassium?.percentage || 0}%</div>
                  </div>
                </div>
              )}
              
              {npkBalance && (
                <div className="chart-container">
                  <NPKChart npkData={npkBalance} />
                </div>
              )}

              {soilMetrics && (
                <div style={{ marginTop: '1rem' }}>
                  <h3 className="card-subtitle">Soil Conditions</h3>
                  <div className="weather-grid">
                    <div className="weather-item">
                      <FaTachometerAlt className="weather-icon" />
                      <div className="weather-info">
                        <h4>Soil EC</h4>
                        <p>{soilMetrics.soilEC} <span className="unit">μS/cm</span></p>
                      </div>
                    </div>
                    <div className="weather-item">
                      <FaThermometerHalf className="weather-icon" />
                      <div className="weather-info">
                        <h4>Soil Temperature</h4>
                        <p>{soilMetrics.soiltemp} <span className="unit">°C</span></p>
                      </div>
                    </div>
                    <div className="weather-item">
                      <FaCloudRain className="weather-icon" />
                      <div className="weather-info">
                        <h4>Soil Moisture</h4>
                        <p>{soilMetrics.soilmoisture} <span className="unit">%</span></p>
                      </div>
                    </div>
                    <div className="weather-item">
                      <FaChartBar className="weather-icon" />
                      <div className="weather-info">
                        <h4>Soil pH</h4>
                        <p>{soilMetrics.soilPH} <span className="unit">pH</span></p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Weather Conditions Card */}
            <div className="dashboard-card dashboard-card-weather">
              <div className="card-header">
                <div className="card-icon" style={{ background: 'linear-gradient(135deg, #2196F3, #1976D2)' }}>
                  <FaWind />
                </div>
                <h2 className="card-title">Weather Conditions</h2>
                <span className={`status-indicator ${getStatusColor(weatherAnalysis?.windCondition)}`}>
                  <FaThermometerHalf style={{ marginRight: '0.5rem' }} />
                  {weatherAnalysis?.windCondition || 'Unknown'}
                </span>
              </div>
              
              {weather && (
                <div className="weather-grid">
                  <div className="weather-item">
                    <FaWind className="weather-icon" />
                    <div className="weather-info">
                      <h4>Wind Speed</h4>
                      <p>{weather.windSpeed} <span className="unit">km/h</span> {weather.windDirection}</p>
                    </div>
                  </div>
                  <div className="weather-item">
                    <FaCloudRain className="weather-icon" />
                    <div className="weather-info">
                      <h4>Rainfall</h4>
                      <p>{weather.totalRainfall} <span className="unit">mm</span></p>
                    </div>
                  </div>
                </div>
              )}
              
              {weather && (
                <div className="chart-container">
                  <WeatherChart weatherData={weather} />
                </div>
              )}
            </div>

            {/* Air Metrics Card */}
            <div className="dashboard-card dashboard-card-air">
              <div className="card-header">
                <div className="card-icon" style={{ background: 'linear-gradient(135deg, #FF5722, #E64A19)' }}>
                  <FaThermometerHalf />
                </div>
                <h2 className="card-title">Air & Radiation</h2>
              </div>
              {airMetrics && (
                <div className="weather-grid">
                  <div className="weather-item">
                    <FaThermometerHalf className="weather-icon" />
                    <div className="weather-info">
                      <h4>Air Temp</h4>
                      <p>{airMetrics.airTemp} <span className="unit">°C</span></p>
                    </div>
                  </div>
                  <div className="weather-item">
                    <FaCloudRain className="weather-icon" />
                    <div className="weather-info">
                      <h4>Air Humidity</h4>
                      <p>{airMetrics.airHumidity} <span className="unit">%</span></p>
                    </div>
                  </div>
                  <div className="weather-item">
                    <FaTachometerAlt className="weather-icon" />
                    <div className="weather-info">
                      <h4>VPD</h4>
                      <p>{airMetrics.vpd} <span className="unit">kPa</span></p>
                    </div>
                  </div>
                  <div className="weather-item">
                    <FaChartBar className="weather-icon" />
                    <div className="weather-info">
                      <h4>Solar Radiation</h4>
                      <p>{airMetrics.solarRad} <span className="unit">W/m²</span></p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* System Health Card */}
            <div className="dashboard-card dashboard-card-health">
              <div className="card-header">
                <div className="card-icon" style={{ background: 'linear-gradient(135deg, #FF9800, #F57C00)' }}>
                  <FaCheckCircle />
                </div>
                <h2 className="card-title">System Health</h2>
                <span className={`status-indicator ${getStatusColor(healthStatus?.status)}`}>
                  <FaCheckCircle style={{ marginRight: '0.5rem' }} />
                  {healthStatus?.status || 'Unknown'}
                </span>
              </div>
              
              <div className="weather-grid">
                <div className="weather-item">
                  <FaSync className="weather-icon" />
                  <div className="weather-info">
                    <h4>Last Update</h4>
                    <p>{healthStatus?.hoursOld ? `${healthStatus.hoursOld}h ago` : 'Unknown'}</p>
                  </div>
                </div>
                <div className="weather-item">
                  <FaThermometerHalf className="weather-icon" />
                  <div className="weather-info">
                    <h4>Data Quality</h4>
                    <p>{healthStatus?.dataQuality || 'Unknown'}</p>
                  </div>
                </div>
              </div>
              
              <div style={{ marginTop: '1rem', padding: '1rem', background: '#f7fafc', borderRadius: '12px' }}>
                <h4 style={{ margin: '0 0 0.5rem 0', color: '#2d3748' }}>Sensor Information</h4>
                <p style={{ margin: '0', fontSize: '0.9rem', color: '#718096' }}>
                  Last reading: {formatTimestamp(sensorData?.timestamp)}
                </p>
                <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.9rem', color: '#718096' }}>
                  Sensor ID: {sensorData?.id?.slice(-8) || 'Unknown'}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Speedometer View */}
        {viewMode === 'speedometer' && soilNutrients && weather && (
          <div className="dashboard-grid dashboard-grid-speedometer">
            {/* NPK card */}
            <div className="dashboard-card">
              <div className="card-header">
                <div className="card-icon" style={{ background: 'linear-gradient(135deg, #4CAF50, #45a049)' }}>
                  <FaLeaf />
                </div>
                <h2 className="card-title">NPK Levels - Speedometer View</h2>
              </div>
              <div className="speedometer-grid">
                <SpeedometerChart 
                  value={soilNutrients.nitrogen} 
                  maxValue={300} 
                  label="Nitrogen" 
                  color="#38a169"
                  unit=" mg/kg"
                />
                <SpeedometerChart 
                  value={soilNutrients.phosphorous} 
                  maxValue={250} 
                  label="Phosphorous" 
                  color="#3182ce"
                  unit=" mg/kg"
                />
                <SpeedometerChart 
                  value={soilNutrients.potassium} 
                  maxValue={300} 
                  label="Potassium" 
                  color="#d69e2e"
                  unit=" mg/kg"
                />
              </div>
            </div>

            {/* Soil metrics card */}
            {soilMetrics && (
              <div className="dashboard-card">
                <div className="card-header">
                  <div
                    className="card-icon"
                    style={{ background: 'linear-gradient(135deg, #FF5722, #E64A19)' }}
                  >
                    <FaChartBar />
                  </div>
                  <h2 className="card-title">Soil Metrics - Speedometer View</h2>
                </div>

                <div className="speedometer-grid">
                  <SpeedometerChart
                    value={soilMetrics.soilmoisture}
                    maxValue={100}
                    label="Soil Moisture"
                    color="#4CAF50"
                    unit=" %"
                  />
                  <SpeedometerChart
                    value={soilMetrics.soiltemp}
                    maxValue={50}
                    label="Soil Temperature"
                    color="#FF9800"
                    unit=" °C"
                  />
                  <SpeedometerChart
                    value={soilMetrics.soilEC}
                    maxValue={2000}
                    label="Soil EC"
                    color="#9C27B0"
                    unit=" uS/cm"
                  />
                  <SpeedometerChart
                    value={soilMetrics.soilPH}
                    maxValue={14}
                    label="Soil pH"
                    color="#03A9F4"
                  />
                </div>
              </div>
            )}

            {/* Air metrics card */}
            {airMetrics && (
              <div className="dashboard-card">
                <div className="card-header">
                  <div
                    className="card-icon"
                    style={{ background: 'linear-gradient(135deg, #FF5722, #E64A19)' }}
                  >
                    <FaThermometerHalf />
                  </div>
                  <h2 className="card-title">Air Metrics - Speedometer View</h2>
                </div>
                <div className="speedometer-grid">
                  <SpeedometerChart
                    value={airMetrics.airTemp}
                    maxValue={60}
                    label="Air Temp"
                    color="#FF5722"
                    unit=" °C"
                  />
                  <SpeedometerChart
                    value={airMetrics.airHumidity}
                    maxValue={100}
                    label="Air Humidity"
                    color="#2196F3"
                    unit=" %"
                  />
                  <SpeedometerChart
                    value={airMetrics.vpd}
                    maxValue={10}
                    label="VPD"
                    color="#9C27B0"
                  />
                  <SpeedometerChart
                    value={airMetrics.solarRad}
                    maxValue={2000}
                    label="Solar Rad"
                    color="#FFC107"
                  />
                </div>
              </div>
            )}

            {/* Weather card */}
            <div className="dashboard-card">
              <div className="card-header">
                <div className="card-icon" style={{ background: 'linear-gradient(135deg, #2196F3, #1976D2)' }}>
                  <FaWind />
                </div>
                <h2 className="card-title">Weather - Speedometer View</h2>
              </div>
              <div className="speedometer-grid">
                <SpeedometerChart 
                  value={weather.windSpeed} 
                  maxValue={50} 
                  label="Wind Speed" 
                  color="#2196F3"
                  unit=" km/h"
                />
                <SpeedometerChart 
                  value={weather.totalRainfall} 
                  maxValue={100} 
                  label="Total Rainfall" 
                  color="#4CAF50"
                  unit=" mm"
                />
              </div>
            </div>
            
          </div>
        )}

        {/* Trends View */}
        {viewMode === 'trends' && (
          <div className="dashboard-grid">
            <div className="dashboard-card" style={{ gridColumn: '1 / -1' }}>
              <div className="card-header">
                <div className="card-icon" style={{ background: 'linear-gradient(135deg, #667eea, #764ba2)' }}>
                  <FaChartLine />
                </div>
                <h2 className="card-title">NPK Trends - 24 Hour History</h2>
              </div>
              <div className="trend-chart-container">
                <TrendChart sensorData={historicalData} type="npk" />
              </div>
            </div>

            {/* Soil Metrics Trends Card */}
            <div className="dashboard-card" style={{ gridColumn: '1 / -1' }}>
              <div className="card-header">
                <div
                  className="card-icon"
                  style={{ background: 'linear-gradient(135deg, #FF5722, #E64A19)' }}
                >
                  <FaChartBar />
                </div>
                <h2 className="card-title">Soil Metrics - 24 Hour History</h2>
              </div>
              <div className="trend-chart-container">
                <TrendChart sensorData={historicalData} type="soil" />
              </div>
            </div>

            {/* Air Metrics Trends Card */}
            <div className="dashboard-card" style={{ gridColumn: '1 / -1' }}>
              <div className="card-header">
                <div
                  className="card-icon"
                  style={{ background: 'linear-gradient(135deg, #FF5722, #E64A19)' }}
                >
                  <FaThermometerHalf />
                </div>
                <h2 className="card-title">Air Metrics - 24 Hour History</h2>
              </div>
              <div className="trend-chart-container">
                <TrendChart sensorData={historicalData} type="air" />
              </div>
            </div>

            <div className="dashboard-card" style={{ gridColumn: '1 / -1' }}>
              <div className="card-header">
                <div className="card-icon" style={{ background: 'linear-gradient(135deg, #2196F3, #1976D2)' }}>
                  <FaWind />
                </div>
                <h2 className="card-title">Weather Trends - 24 Hour History</h2>
              </div>
              <div className="trend-chart-container">
                <TrendChart sensorData={historicalData} type="weather" />
              </div>
            </div>

          </div>
        )}

        {/* Auto-refresh indicator */}
        <div className="refresh-info">
          <FaSync className="refresh-icon" />
          Auto-refreshing every 30 seconds
        </div>

        {lastUpdated && (
          <div className="last-updated">
            Dashboard last updated: {lastUpdated.toLocaleString()}
          </div>
        )}

        {/* Download Section - Only visible in current view */}
        {viewMode === 'current' && (
          <div className="download-section">
            <h3 className="download-title">Download Sensor Data</h3>
            <div className="download-options">
              <button 
                className="download-button" 
                onClick={downloadCurrentData}
                disabled={isDownloading}
              >
                {isDownloading ? (
                  <>
                    <span className="spinner"></span>
                    Downloading...
                  </>
                ) : (
                  <>
                    <FaDownload className="download-icon" />
                    Download Current Data
                  </>
                )}
              </button>
              
              <div className="download-historical">
                <button className="download-button" onClick={toggleDatePicker}>
                  <FaCalendarAlt className="download-icon" />
                  Download Historical Data
                </button>
                
                {showDatePicker && (
                  <>
                    <div className="date-picker-overlay" onClick={() => setShowDatePicker(false)}></div>
                    <div className="date-picker-container" ref={datePickerRef}>
                      <div className="date-picker-content">
                        <div className="date-range-inputs">
                          <div className="date-input-group">
                            <label htmlFor="from-date">From Date:</label>
                            <input 
                              type="date" 
                              id="from-date"
                              className="date-input"
                              value={fromDate.toISOString().split('T')[0]}
                              onChange={handleFromDateChange}
                              max={new Date().toISOString().split('T')[0]}
                            />
                          </div>
                          <div className="date-input-group">
                            <label htmlFor="to-date">To Date:</label>
                            <input 
                              type="date" 
                              id="to-date"
                              className="date-input"
                              value={toDate.toISOString().split('T')[0]}
                              onChange={handleToDateChange}
                              max={new Date().toISOString().split('T')[0]}
                            />
                          </div>
                        </div>
                        <button 
                          className="download-button download-button-small"
                          onClick={() => {
                            downloadHistoricalData();
                            setShowDatePicker(false);
                          }}
                          disabled={isDownloading}
                        >
                          {isDownloading ? (
                            <>
                              <span className="spinner"></span>
                              Downloading...
                            </>
                          ) : (
                            <>
                              <FaFileExport className="download-icon" />
                              Export Date Range
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="dashboard-footer">
          <div className="footer-content">
            <p className="footer-text">A Product of <strong>QodeAris</strong></p>
            <div className="footer-links">
              <a href="https://www.qodearis.com" target="_blank" rel="noopener noreferrer" className="footer-link">
                <FaGlobe className="footer-icon" />
                www.qodearis.com
              </a>
              <a href="mailto:hello@qodearis.com" className="footer-link">
                <FaEnvelope className="footer-icon" />
                hello@qodearis.com
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SensorDashboard;
